import { useEffect, useRef, useState } from "react";
import { useTheme } from "../ThemeProvider";
import YesNoField from "./YesNoField";
import InventarioMueblesFicha from "./InventarioMueblesFicha";
import usePermiso from "../../hooks/usePermiso";
import { usePropiedadUi } from "../../hooks/usePropiedadUi";
import { formatoAlturaPlantaCrm, parseAlturaPlantaCrm } from "../../utils/formatoAlturaPlantaCrm";

function DesplegableContador({ value, onChange, selectStyle, isDark, c, disabled = false }) {
  const [abierto, setAbierto] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!abierto) return undefined;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setAbierto(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [abierto]);

  const texto = value === "" || value == null ? "—" : String(value);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          if (disabled) return;
          setAbierto((v) => !v);
        }}
        disabled={disabled}
        title={disabled ? "No tienes permiso para modificar" : undefined}
        style={{
          ...selectStyle,
          width: "100%",
          textAlign: "left",
          cursor: disabled ? "not-allowed" : "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          color: value === "" || value == null ? c.textMuted : c.textPrimary,
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <span>{texto}</span>
        <span style={{ fontSize: 10, opacity: 0.7 }}>{abierto ? "▲" : "▼"}</span>
      </button>
      {abierto && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            zIndex: 50,
            minWidth: "100%",
            maxHeight: 220,
            overflowY: "auto",
            background: isDark ? "#111827" : "#fff",
            border: `1px solid ${c.chipBorder}`,
            borderRadius: 8,
            boxShadow: isDark ? "0 8px 24px rgba(0,0,0,0.45)" : "0 8px 24px rgba(0,0,0,0.12)",
            padding: "4px 0",
          }}
        >
          <button
            type="button"
            onClick={() => { onChange(""); setAbierto(false); }}
            style={{
              display: "block", width: "100%", textAlign: "left",
              padding: "8px 12px", border: "none", background: "transparent",
              fontSize: 12, color: c.textMuted, cursor: "pointer",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
          >
            —
          </button>
          {Array.from({ length: 11 }, (_, i) => i).map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => { onChange(n); setAbierto(false); }}
              style={{
                display: "block", width: "100%", textAlign: "left",
                padding: "8px 12px", border: "none", background: "transparent",
                fontSize: 12, color: c.textPrimary, cursor: "pointer",
                fontWeight: String(value) === String(n) ? 700 : 400,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
            >
              {n}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function NumField({ label, name, form, cambiar, inputStyle, labelStyle, obsColor, disabled = false, tv = (x) => x }) {
  return (
    <div>
      <label style={labelStyle}>{tv(label)}</label>
      <input
        type="number"
        name={name}
        value={form[name] ?? ""}
        onChange={cambiar}
        disabled={disabled}
        title={disabled ? "No tienes permiso para modificar" : undefined}
        style={{ ...inputStyle, opacity: disabled ? 0.6 : 1, cursor: disabled ? "not-allowed" : "text" }}
      />
      <input type="text" name={name + "_obs"} value={form[name + "_obs"] || ""} onChange={cambiar}
        placeholder={tv("Obs.")} tabIndex={-1} disabled={disabled}
        style={{ ...inputStyle, fontSize: 11, padding: "5px 10px", marginTop: 4, color: obsColor, opacity: disabled ? 0.6 : 1, cursor: disabled ? "not-allowed" : "text" }} />
    </div>
  );
}

function TextField({ label, name, form, cambiar, inputStyle, labelStyle, obsColor, placeholder, disabled = false, tv = (x) => x, formatValue, parseValue }) {
  const raw = form[name] ?? "";
  const shown = formatValue ? formatValue(raw) : (raw || "");
  return (
    <div>
      <label style={labelStyle}>{tv(label)}</label>
      <input
        type="text"
        name={name}
        value={shown}
        onChange={(e) => {
          if (!parseValue) {
            cambiar(e);
            return;
          }
          const next = parseValue(e.target.value);
          cambiar({ ...e, target: { ...e.target, name, value: next } });
        }}
        placeholder={placeholder ? tv(placeholder) : undefined}
        disabled={disabled}
        title={disabled ? "No tienes permiso para modificar" : undefined}
        style={{ ...inputStyle, opacity: disabled ? 0.6 : 1, cursor: disabled ? "not-allowed" : "text" }}
      />
      <input type="text" name={name + "_obs"} value={form[name + "_obs"] || ""} onChange={cambiar}
        placeholder={tv("Obs.")} tabIndex={-1} disabled={disabled}
        style={{ ...inputStyle, fontSize: 11, padding: "5px 10px", marginTop: 4, color: obsColor, opacity: disabled ? 0.6 : 1, cursor: disabled ? "not-allowed" : "text" }} />
    </div>
  );
}

function CountField({ label, name, form, cambiar, cambiarDirecto, selectStyle, isDark, c, inputStyle, labelStyle, obsColor, disabled = false, tv = (x) => x }) {
  return (
    <div>
      <label style={labelStyle}>{tv(label)}</label>
      <DesplegableContador
        value={form[name] ?? ""}
        onChange={(v) => cambiarDirecto(name, v === "" ? "" : v)}
        selectStyle={selectStyle}
        isDark={isDark}
        c={c}
        disabled={disabled}
      />
      <input type="text" name={name + "_obs"} value={form[name + "_obs"] || ""} onChange={cambiar}
        placeholder={tv("Obs.")} tabIndex={-1} disabled={disabled}
        style={{ ...inputStyle, fontSize: 11, padding: "5px 10px", marginTop: 4, color: obsColor, opacity: disabled ? 0.6 : 1, cursor: disabled ? "not-allowed" : "text" }} />
    </div>
  );
}

export default function SeccionDistribucion(props) {
  const {
    form, cambiar, cambiarDirecto, auxiliares, superficies, setSuperficies,
    propiedadId = null,
  } = props;
  const { tv } = usePropiedadUi(props);
  const { theme } = useTheme();
  const c = theme.colors;
  const isDark = theme.name === "dark";
  const { puede } = usePermiso();
  const [panelInventario, setPanelInventario] = useState(null); // null | 'ver' | 'editar'
  const puedeVerNumDormitorios = puede("num_dormitorios", "ver");
  const puedeModificarNumDormitorios = puede("num_dormitorios", "modificar");
  const puedeVerNumBanos = puede("num_banos", "ver");
  const puedeModificarNumBanos = puede("num_banos", "modificar");
  const puedeVerPlantas = puede("plantas", "ver");
  const puedeModificarPlantas = puede("plantas", "modificar");
  const puedeVerCarpinteria = puede("carpinteria", "ver");
  const puedeModificarCarpinteria = puede("carpinteria", "modificar");
  const puedeVerSuelos = puede("suelos", "ver");
  const puedeModificarSuelos = puede("suelos", "modificar");
  const puedeVerCocina = puede("cocina", "ver");
  const puedeModificarCocina = puede("cocina", "modificar");
  const puedeVerDistribSuperficies = puede("distrib_superficies", "ver");
  const puedeModificarDistribSuperficies = puede("distrib_superficies", "modificar");
  const puedeVerNumAseos = puede("num_aseos", "ver");
  const puedeModificarNumAseos = puede("num_aseos", "modificar");
  const puedeVerNumSalones = puede("num_salones", "ver");
  const puedeModificarNumSalones = puede("num_salones", "modificar");
  const puedeVerNumComedores = puede("num_comedores", "ver");
  const puedeModificarNumComedores = puede("num_comedores", "modificar");
  const puedeVerAlturaPlanta = puede("altura_planta", "ver");
  const puedeModificarAlturaPlanta = puede("altura_planta", "modificar");
  const puedeVerNumEntradas = puede("num_entradas", "ver");
  const puedeModificarNumEntradas = puede("num_entradas", "modificar");
  const puedeVerArmarios = puede("armarios", "ver");
  const puedeModificarArmarios = puede("armarios", "modificar");
  const puedeVerAmueblado = puede("amueblado", "ver");
  const puedeModificarAmueblado = puede("amueblado", "modificar");

  const carpInt = auxiliares["carpinteria-interior"] || [];
  const carpExt = auxiliares["carpinteria-exterior"] || [];
  const suelos = auxiliares["suelos"] || [];
  const muebles = auxiliares["muebles"] || [];

  const [nSup, setNSup] = useState({ nombre: "", observaciones: "" });
  const [editIdx, setEditIdx] = useState(null);

  const limpiarFormSup = () => {
    setEditIdx(null);
    setNSup({ nombre: "", observaciones: "" });
  };

  const addSup = () => {
    if (!nSup.nombre) return;
    const row = {
      nombre: nSup.nombre,
      superficie: "",
      altura: "",
      observaciones: nSup.observaciones || "",
    };
    if (editIdx != null && editIdx >= 0 && editIdx < superficies.length) {
      const next = superficies.slice();
      next[editIdx] = {
        ...row,
        orden: superficies[editIdx]?.orden != null ? superficies[editIdx].orden : editIdx,
      };
      setSuperficies(next);
    } else {
      setSuperficies([...superficies, { ...row, orden: superficies.length }]);
    }
    limpiarFormSup();
  };
  const delSup = (i) => {
    setSuperficies(superficies.filter((_, idx) => idx !== i));
    if (editIdx === i) limpiarFormSup();
    else if (editIdx != null && editIdx > i) setEditIdx(editIdx - 1);
  };
  const editSup = (i) => {
    const s = superficies[i];
    if (!s) return;
    setEditIdx(i);
    setNSup({
      nombre: s.nombre || "",
      observaciones: s.observaciones || "",
    });
  };

  const labelStyle = {
    fontSize: 11, fontWeight: 600, color: c.textMuted,
    textTransform: "uppercase", letterSpacing: "0.04em",
    marginBottom: 6, display: "block",
  };
  const inputStyle = {
    padding: "8px 12px", border: `1px solid ${c.chipBorder}`,
    borderRadius: 8, fontSize: 13, color: c.textPrimary,
    background: isDark ? '#0a0e16' : '#dceeff', width: "100%", outline: "none",
    boxShadow: isDark ? 'inset 0 3px 6px rgba(0,0,0,0.5), inset 0 1px 3px rgba(0,0,0,0.3)' : 'inset 0 3px 6px rgba(100,140,200,0.18), inset 0 1px 3px rgba(80,120,180,0.13)',
  };
  const selectStyle = { ...inputStyle, cursor: "pointer" };
  const obsColor = c.textSecondary;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

      {/* TABLA DE SUPERFICIES */}
      {puedeVerDistribSuperficies && (
      <div>
        <label style={labelStyle}>{tv("Plantas / distribución")}</label>

        {superficies.length > 0 && (
          <div style={{
            border: `1px solid ${c.sectionBorder}`,
            borderRadius: 8,
            overflow: "hidden",
            marginBottom: 10,
          }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: isDark ? "#0e1117" : "#f8fafc" }}>
                  {["Nombre", "Distribución", ""].map(h => (
                    <th key={h || "acciones"} style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, fontWeight: 700, color: c.textMuted, textTransform: "uppercase" }}>{h ? tv(h) : ""}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {superficies.map((s, i) => (
                  <tr key={i} style={{
                    borderTop: `1px solid ${c.sectionBorder}`,
                    background: editIdx === i ? (isDark ? "rgba(22,163,74,0.14)" : "rgba(22,163,74,0.08)") : undefined,
                  }}>
                    <td style={{ padding: "8px 12px", fontSize: 13, color: c.textPrimary, fontWeight: 600 }}>
                      {s.nombre}
                      {editIdx === i && (
                        <span style={{ marginLeft: 6, fontSize: 11, color: "#16a34a", fontWeight: 700 }}>({tv("editando")})</span>
                      )}
                    </td>
                    <td style={{ padding: "8px 12px", fontSize: 13, color: c.textMuted }}>{s.observaciones || "—"}</td>
                    <td style={{ padding: "8px 12px", textAlign: "right", whiteSpace: "nowrap" }}>
                      <button type="button" onClick={() => editSup(i)}
                        style={{ fontSize: 12, color: "#16a34a", background: "none", border: "none", cursor: "pointer", padding: "2px 6px", fontWeight: 700 }}>
                        {tv("Editar")}
                      </button>
                      <button type="button" onClick={() => delSup(i)}
                        style={{ fontSize: 12, color: c.accentRed, background: "none", border: "none", cursor: "pointer", padding: "2px 6px" }}>
                        {tv("Eliminar")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Añadir nueva planta */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div style={{ flex: 2, minWidth: 140 }}>
            <label style={{ ...labelStyle, marginBottom: 4 }}>{tv("Nombre")}</label>
            <input type="text" value={nSup.nombre} onChange={e => setNSup({ ...nSup, nombre: e.target.value })}
              placeholder={tv("Ej: Vivienda / Planta baja")} style={{ ...inputStyle, width: "auto", display: "block", flex: 1 }} />
          </div>
          <div style={{ flex: 3, minWidth: 180 }}>
            <label style={{ ...labelStyle, marginBottom: 4 }}>{tv("Distribución")}</label>
            <input type="text" value={nSup.observaciones} onChange={e => setNSup({ ...nSup, observaciones: e.target.value })}
              placeholder={tv("Ej: salón, cocina, 2 dormitorios…")} style={inputStyle} />
          </div>
          <button type="button" onClick={addSup} disabled={!puedeModificarDistribSuperficies}
            title={!puedeModificarDistribSuperficies ? "No tienes permiso para modificar" : undefined}
            style={{
              padding: "8px 16px",
              background: puedeModificarDistribSuperficies ? "linear-gradient(135deg, #2563eb, #1d4ed8)" : "#64748b",
              color: "#fff",
              border: "none", borderRadius: 8,
              fontSize: 13, fontWeight: 600, cursor: puedeModificarDistribSuperficies ? "pointer" : "not-allowed",
              whiteSpace: "nowrap",
              height: 36,
              opacity: puedeModificarDistribSuperficies ? 1 : 0.6,
            }}>
            {editIdx != null ? tv("💾 Guardar cambios") : tv("Guardar")}
          </button>
          {editIdx != null && (
            <button type="button" onClick={limpiarFormSup}
              style={{
                padding: "8px 12px",
                background: "transparent",
                color: c.textMuted,
                border: `1px solid ${c.chipBorder}`,
                borderRadius: 8,
                fontSize: 12, fontWeight: 600, cursor: "pointer",
                height: 36,
              }}>
              {tv("Cancelar")}
            </button>
          )}
        </div>
      </div>
      )}

      <div style={{ borderTop: `1px solid ${c.sectionBorder}` }} />

      {/* HABITACIONES */}
      <div>
        <label style={labelStyle}>{tv("Distribución de habitaciones")}</label>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(8, minmax(0, 1fr))", gap: 10 }}>
          {puedeVerNumDormitorios && <NumField label="Dormitorios" name="num_dormitorios" form={form} cambiar={cambiar} inputStyle={inputStyle} labelStyle={labelStyle} obsColor={obsColor} disabled={!puedeModificarNumDormitorios} tv={tv} />}
          {puedeVerNumBanos && <NumField label="Baños" name="num_banos" form={form} cambiar={cambiar} inputStyle={inputStyle} labelStyle={labelStyle} obsColor={obsColor} disabled={!puedeModificarNumBanos} tv={tv} />}
          {puedeVerNumAseos && <NumField label="Aseos" name="num_aseos" form={form} cambiar={cambiar} inputStyle={inputStyle} labelStyle={labelStyle} obsColor={obsColor} disabled={!puedeModificarNumAseos} tv={tv} />}
          {puedeVerNumSalones && <CountField label="Salones" name="num_salones" form={form} cambiar={cambiar} cambiarDirecto={cambiarDirecto} selectStyle={selectStyle} isDark={isDark} c={c} inputStyle={inputStyle} labelStyle={labelStyle} obsColor={obsColor} disabled={!puedeModificarNumSalones} tv={tv} />}
          {puedeVerNumComedores && <CountField label="Comedores" name="num_comedores" form={form} cambiar={cambiar} cambiarDirecto={cambiarDirecto} selectStyle={selectStyle} isDark={isDark} c={c} inputStyle={inputStyle} labelStyle={labelStyle} obsColor={obsColor} disabled={!puedeModificarNumComedores} tv={tv} />}
          {puedeVerPlantas && <NumField label="Plantas del inmueble" name="plantas_inmueble" form={form} cambiar={cambiar} inputStyle={inputStyle} labelStyle={labelStyle} obsColor={obsColor} disabled={!puedeModificarPlantas} tv={tv} />}
          {puedeVerPlantas && <NumField label="Plantas del edificio" name="plantas_edificio" form={form} cambiar={cambiar} inputStyle={inputStyle} labelStyle={labelStyle} obsColor={obsColor} disabled={!puedeModificarPlantas} tv={tv} />}
          {puedeVerAlturaPlanta && <TextField label="Altura planta" name="altura_planta" form={form} cambiar={cambiar}
            inputStyle={inputStyle} labelStyle={labelStyle} obsColor={obsColor} placeholder="Ej: 3 o Planta baja" disabled={!puedeModificarAlturaPlanta} tv={tv}
            formatValue={formatoAlturaPlantaCrm} parseValue={parseAlturaPlantaCrm} />}
        </div>
      </div>

      <div style={{ borderTop: `1px solid ${c.sectionBorder}` }} />

      {/* ACABADOS */}
      <div>
        <label style={labelStyle}>{tv("Acabados e interiores")}</label>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 12 }}>
          {puedeVerCarpinteria && (
          <div>
            <label style={{ ...labelStyle, textTransform: "none", fontSize: 12 }}>{tv("Carp. Interior")}</label>
            <select
              name="carpinteria_int_id"
              value={form.carpinteria_int_id || ""}
              onChange={cambiar}
              disabled={!puedeModificarCarpinteria}
              title={!puedeModificarCarpinteria ? "No tienes permiso para modificar" : undefined}
              style={{ ...selectStyle, opacity: puedeModificarCarpinteria ? 1 : 0.6, cursor: puedeModificarCarpinteria ? "pointer" : "not-allowed" }}
            >
              <option value="">{tv("Seleccionar")}</option>
              {carpInt.map(c2 => <option key={c2.id} value={c2.id}>{c2.nombre}</option>)}
            </select>
          </div>
          )}
          {puedeVerCarpinteria && (
          <div>
            <label style={{ ...labelStyle, textTransform: "none", fontSize: 12 }}>{tv("Carp. Exterior")}</label>
            <select
              name="carpinteria_ext_id"
              value={form.carpinteria_ext_id || ""}
              onChange={cambiar}
              disabled={!puedeModificarCarpinteria}
              title={!puedeModificarCarpinteria ? "No tienes permiso para modificar" : undefined}
              style={{ ...selectStyle, opacity: puedeModificarCarpinteria ? 1 : 0.6, cursor: puedeModificarCarpinteria ? "pointer" : "not-allowed" }}
            >
              <option value="">{tv("Seleccionar")}</option>
              {carpExt.map(c2 => <option key={c2.id} value={c2.id}>{c2.nombre}</option>)}
            </select>
          </div>
          )}
          {puedeVerSuelos && (
          <div>
            <label style={{ ...labelStyle, textTransform: "none", fontSize: 12 }}>{tv("Suelos")}</label>
            <select
              name="suelos_id"
              value={form.suelos_id || ""}
              onChange={cambiar}
              disabled={!puedeModificarSuelos}
              title={!puedeModificarSuelos ? "No tienes permiso para modificar" : undefined}
              style={{ ...selectStyle, opacity: puedeModificarSuelos ? 1 : 0.6, cursor: puedeModificarSuelos ? "pointer" : "not-allowed" }}
            >
              <option value="">{tv("Seleccionar")}</option>
              {suelos.map(s2 => <option key={s2.id} value={s2.id}>{s2.nombre}</option>)}
            </select>
          </div>
          )}
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginTop: 16, alignItems: "flex-end" }}>
          {puedeVerCocina && (
          <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
            <div style={{ minWidth: 120 }}>
              <CountField label="Cocinas" name="num_cocinas" form={form} cambiar={cambiar} cambiarDirecto={cambiarDirecto} selectStyle={selectStyle} isDark={isDark} c={c} inputStyle={inputStyle} labelStyle={labelStyle} obsColor={obsColor} disabled={!puedeModificarCocina} tv={tv} />
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
              <YesNoField name="cocina_amueblada" label="Cocina amueblada" form={form} cambiarDirecto={cambiarDirecto} compact
                disabled={!puedeModificarCocina}
                onChangeSideEffect={(v) => { if (v !== true) cambiarDirecto("cocina_amueblada_obs", ""); }} />
              {form.cocina_amueblada === true && (
                <input type="text" name="cocina_amueblada_obs" value={form.cocina_amueblada_obs || ""}
                  onChange={cambiar} placeholder={tv("Detalles...")}
                  disabled={!puedeModificarCocina}
                  title={!puedeModificarCocina ? "No tienes permiso para modificar" : undefined}
                  style={{ ...inputStyle, width: 160, fontSize: 12, padding: "6px 10px", opacity: puedeModificarCocina ? 1 : 0.6, cursor: puedeModificarCocina ? "text" : "not-allowed" }} />
              )}
            </div>
          </div>
          )}
          {puedeVerNumEntradas && (
          <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
            <div style={{ minWidth: 120 }}>
              <CountField label="Entradas" name="num_entradas" form={form} cambiar={cambiar} cambiarDirecto={cambiarDirecto} selectStyle={selectStyle} isDark={isDark} c={c} inputStyle={inputStyle} labelStyle={labelStyle} obsColor={obsColor} disabled={!puedeModificarNumEntradas} tv={tv} />
            </div>
            {puedeVerArmarios && (
            <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
              <YesNoField name="armarios" label="Armarios" form={form} cambiarDirecto={cambiarDirecto} compact
                disabled={!puedeModificarArmarios}
                onChangeSideEffect={(v) => { if (v !== true) cambiarDirecto("armarios_obs", ""); }} />
              {form.armarios === true && (
                <input type="text" name="armarios_obs" value={form.armarios_obs || ""}
                  onChange={cambiar} placeholder={tv("Detalles...")}
                  disabled={!puedeModificarArmarios}
                  title={!puedeModificarArmarios ? "No tienes permiso para modificar" : undefined}
                  style={{ ...inputStyle, width: 160, fontSize: 12, padding: "6px 10px", opacity: puedeModificarArmarios ? 1 : 0.6, cursor: puedeModificarArmarios ? "text" : "not-allowed" }} />
              )}
            </div>
            )}
          </div>
          )}
          {puedeVerArmarios && !puedeVerNumEntradas && (
          <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
            <YesNoField name="armarios" label="Armarios" form={form} cambiarDirecto={cambiarDirecto} compact
              disabled={!puedeModificarArmarios}
              onChangeSideEffect={(v) => { if (v !== true) cambiarDirecto("armarios_obs", ""); }} />
            {form.armarios === true && (
              <input type="text" name="armarios_obs" value={form.armarios_obs || ""}
                onChange={cambiar} placeholder={tv("Detalles...")}
                disabled={!puedeModificarArmarios}
                style={{ ...inputStyle, width: 160, fontSize: 12, padding: "6px 10px", opacity: puedeModificarArmarios ? 1 : 0.6 }} />
            )}
          </div>
          )}
          {puedeVerAmueblado && (
          <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "stretch" }}>
            <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
              <YesNoField name="amueblado" label="Amueblado" form={form} cambiarDirecto={cambiarDirecto} compact
                disabled={!puedeModificarAmueblado}
                onChangeSideEffect={(v) => {
                  if (v === true) return;
                  cambiarDirecto("muebles_id", "");
                  // Amueblado=NO → sin inventario en ficha/captación/encargo
                  cambiarDirecto("inventario_muebles", null);
                  setPanelInventario(null);
                }} />
              {form.amueblado === true && (
                <select name="muebles_id" value={form.muebles_id || ""} onChange={cambiar}
                  disabled={!puedeModificarAmueblado}
                  title={!puedeModificarAmueblado ? "No tienes permiso para modificar" : undefined}
                  style={{ ...selectStyle, width: 160, padding: "6px 10px", fontSize: 12, opacity: puedeModificarAmueblado ? 1 : 0.6, cursor: puedeModificarAmueblado ? "pointer" : "not-allowed" }}>
                  <option value="">{tv("Tipo...")}</option>
                  {muebles.map(m => <option key={m.id} value={m.id}>{m.nombre}</option>)}
                </select>
              )}
              {form.amueblado === true && (
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", paddingBottom: 2 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: c.textMuted, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                    {tv("Inventario")}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPanelInventario((m) => (m === "ver" ? null : "ver"))}
                    style={{
                      padding: "5px 10px",
                      borderRadius: 7,
                      border: `1px solid ${panelInventario === "ver" ? "#8b5cf6" : c.border}`,
                      background: panelInventario === "ver" ? (isDark ? "rgba(139,92,246,0.25)" : "#f3e8ff") : c.bgInput,
                      color: panelInventario === "ver" ? "#8b5cf6" : c.textPrimary,
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: "pointer",
                      fontFamily: "inherit",
                    }}
                  >
                    {tv("Ver")}
                  </button>
                  <button
                    type="button"
                    disabled={!puedeModificarAmueblado}
                    onClick={() => setPanelInventario((m) => (m === "editar" ? null : "editar"))}
                    title={!puedeModificarAmueblado ? "No tienes permiso para modificar" : undefined}
                    style={{
                      padding: "5px 10px",
                      borderRadius: 7,
                      border: `1px solid ${panelInventario === "editar" ? "#8b5cf6" : c.border}`,
                      background: panelInventario === "editar" ? (isDark ? "rgba(139,92,246,0.25)" : "#f3e8ff") : c.bgInput,
                      color: panelInventario === "editar" ? "#8b5cf6" : c.textPrimary,
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: puedeModificarAmueblado ? "pointer" : "not-allowed",
                      opacity: puedeModificarAmueblado ? 1 : 0.55,
                      fontFamily: "inherit",
                    }}
                  >
                    {tv("Editar")}
                  </button>
                </div>
              )}
            </div>
            {form.amueblado === true && panelInventario && (
              <InventarioMueblesFicha
                modo={panelInventario}
                inventarioInicial={form.inventario_muebles || null}
                propiedadId={propiedadId || form.id || null}
                puedeEditar={puedeModificarAmueblado}
                onClose={() => setPanelInventario(null)}
                onSaved={(inv) => {
                  cambiarDirecto("inventario_muebles", inv);
                }}
              />
            )}
          </div>
          )}
        </div>
      </div>

    </div>
  );
}
